import { cloneDeep, isEmpty, isUndefined } from 'lodash';
import moment from 'moment';
import dayjs from 'dayjs';
import { sumBy } from 'lodash';
import { AnnualContributionDetails, CompositionEntry } from '../common/types/plan-overview-entity';
import { format, isValid, parseISO } from 'date-fns';
import {
    CallRunpipeArgs,
    CallRunpipePropsType,
    Flow2GoalPriorityEnum,
    goalCategoryPortfolioMappingsType,
    InvestmentSourcesType,
    ReRunOptionType
} from '../common/types';
import store from '../redux/store';
import {
    DEFAULT_RETIREMENT_AGE,
    iwsGoalTypeMapper,
    DEFAULT_CURRENCY_SYMBOL,
    DEFAULT_CURRENCY_LOCALE,
    DEFAULT_GOAL_CATEGORY,
    EWS_DURATION_KEYS,
    EWS_DURATION_MAP,
    EwsDuration,
    EwsDurationKey,
    TermTabType,
    EWS_TERM_TAB_KEY_MAP,
    FEATURE_NAMES,
    appFlowDictionary,
    getEwsDurations
} from '../common/constants';
import { TargetDateKeys } from '../common/types';
import { AdviceGoalPayloadType } from '../features/client-goals';
import { runpipe } from '../services/clients/runpipe';
import { goalcalculator } from '../services/clients/goalcalculator';
import { mpgoalcalculator } from '../services/clients/mpgoalcalculator';
import { ews, runpipeProxy } from '../services/clients/ews';
import { saveGoal, syncAdviceGoals, getPlanExecution } from '../features/client-goals';
import {
    setEwsMaps,
    setAffectedEwsTerms,
    setEwsStepperCtaStatus,
    computeEwsStepperCtaStatus,
    ONBOARDING_BASELINE_CONTINUITY_GOAL
} from '../features/ews';
import type { AdviceContinuityState, AdviceContinuityGoal } from '../features/ews';
import { InvestmentFundOptions, logEventToBackEnd, setSelectedFundingTermIndex } from '../features/global/globalSlice';
import { addAgeToDate } from '../common/helper';
import { uipApiInstance } from '../services';
import { setEnableConfirmConsoleNavigation } from '../features/client';
import { getRiskProfile } from './risk-profile';
import { CONTRIBUTION_FREQUENCY } from '../common/contributionPeriodOptionRules';
import { runUnifiedEwsStream } from './ewsUnifiedEventStream';

// const currentState = store.getState();

export function getInitials(name: string): string {
    let initials;
    const splittedName: any = name.split(' ').filter(Boolean);

    if (!isUndefined(splittedName) && splittedName.length > 1) {
        initials = splittedName[0].charAt(0) + splittedName[splittedName.length - 1].charAt(0);
    } else {
        initials = name.substring(0, 2);
    }
    return initials.toUpperCase();
}

export function deepEqual(obj1: any, obj2: any) {
    // Check if both are objects and not null
    if (typeof obj1 === 'object' && obj1 !== null && typeof obj2 === 'object' && obj2 !== null) {
        const keys1 = Object.keys(obj1).sort();
        const keys2 = Object.keys(obj2).sort();

        // Check if keys length is the same
        if (keys1.length !== keys2.length) {
            return false;
        }

        // Check keys and values
        for (let key of keys1) {
            if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
                return false;
            }
        }
        return true; // Objects are equal
    } else {
        // If not objects, do a strict comparison
        return obj1 === obj2;
    }
}

export function calculateYearsBetweenDates(startDate: any, dateStr: string) {
    if (!startDate || !dateStr) {
        return '0';
    }
    const [day, month, year] = dateStr?.split('-').map(Number);
    let start = startDate;
    if (typeof startDate === 'string') {
        const parts = startDate?.split('-'); // Split into [day, month, year]
        const reformattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`; // "2023-05-26"
        const newStartDate = new Date(reformattedDate);
        start = new Date(newStartDate);
    }
    const endDate = new Date(year, month - 1, day);
    const end = new Date(endDate);
    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    if (months < 0) {
        years--;
        months += 12;
    }
    const fractionalYears = years + months / 12;
    return fractionalYears.toFixed(1);
}

export const getCurrentYear = () => {
    const year = moment().year();

    return year.toString();
};
// The below mapper needs to be updated to add more countries
const dateCountryMapper: any = {
    US: 'mm/dd/yyyy'
};

export const shortDateFormat = () => {
    const currentState = store.getState();
    return dateCountryMapper[currentState?.global?.globalConfig?.countryCode] || 'mm/dd/yyyy';
};

export const isValidDate = (date: string | Date): boolean => {
    if (typeof date === 'string') {
        return isValid(parseISO(date));
    }
    return isValid(date);
};
export const toShortDate = (date: string) => {
    const newDate = new Date(date);
    return isValidDate(newDate) ? format(newDate, shortDateFormat()) : null;
};

export function formatCurrency(number = 0, noDecimal = false): string {
    let currencySymbol = store.getState()?.global?.globalConfig?.currencySymbol || DEFAULT_CURRENCY_SYMBOL;
    let currencyLocale = store.getState()?.global?.globalConfig?.currencyLocale || DEFAULT_CURRENCY_LOCALE;

    if (currencySymbol === 'CAD' && currencyLocale === 'en-CA') {
        return `CAD ${new Intl.NumberFormat(currencyLocale, {
            style: 'currency',
            currency: currencySymbol,
            minimumFractionDigits: noDecimal ? 0 : 2,
            maximumFractionDigits: noDecimal ? 0 : 2
        }).format(number)}`;
    }
    return new Intl.NumberFormat(currencyLocale, {
        style: 'currency',
        currency: currencySymbol,
        minimumFractionDigits: noDecimal ? 0 : 2,
        maximumFractionDigits: noDecimal ? 0 : 2
    }).format(number);
}

export function formatNumber(number = 0): string {
    let formattedNumber = new Intl.NumberFormat().format(number);
    return formattedNumber;
}

// Abbreviated currency (e.g. $1.5M, $80K) for compact table cells; keeps the global
// currency symbol/locale so it matches formatCurrency. Accepts comma-formatted strings.
export function formatCompactCurrency(value: number | string = 0): string {
    const numericValue = typeof value === 'number' ? value : Number(value.replace(/[^.0-9-]/g, ''));
    if (!Number.isFinite(numericValue)) return '';
    const currencySymbol = store.getState()?.global?.globalConfig?.currencySymbol || DEFAULT_CURRENCY_SYMBOL;
    const currencyLocale = store.getState()?.global?.globalConfig?.currencyLocale || DEFAULT_CURRENCY_LOCALE;
    const formatted = new Intl.NumberFormat(currencyLocale, {
        style: 'currency',
        currency: currencySymbol,
        notation: 'compact',
        maximumFractionDigits: 1
    })
        .format(numericValue)
        // Drop the (possibly non-breaking) space some locales add before the K/M unit.
        .replace(/(\d)[\s\u00A0]+(?=\D)/g, '$1')
        .toUpperCase();
    return currencySymbol === 'CAD' && currencyLocale === 'en-CA' ? `CAD ${formatted}` : formatted;
}

export const spaceToHyphen = (phrase: string) => {
    let hyphenatedString = '';
    if (!isEmpty) {
        hyphenatedString = phrase.split('').join('-');
    }
    return hyphenatedString;
};

export const formatCurrencyOrYearWithPlus = (optionValue: ReRunOptionType['optionValue']) => {
    if (typeof optionValue === 'number') return `+${formatCurrency(optionValue)}`;
    return `+${optionValue}`;
};

export const formatCurrencyOrYearWithMinus = (optionValue: ReRunOptionType['optionValue']) => {
    if (typeof optionValue === 'number') return `-${formatCurrency(optionValue)}`;
    return `-${optionValue}`;
};

// export const formatCurrencyDollar = (num: any, doNotAllowZero = true, showDollar = true) => {
//     let value = num.toString();
//     value = value.replace(/[^.0-9]/g, '');

//     if (doNotAllowZero) {
//         value = value.replace(/\b0+/g, '');
//     }

//     let splittedValue = value.split('.');

//     if (splittedValue[1]?.length > 2) {
//         splittedValue[1] = splittedValue[1][0] + splittedValue[1][1];
//         value = [splittedValue[0], splittedValue[1]].join('.');
//     }

//     if (splittedValue?.length > 2) {
//         value = [splittedValue[0], splittedValue[1]].join('.');
//     }

//     value = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

//     return showDollar ? '$' + (value || 0) : value;
// };

export const removeNonNumeric = (num: any) => {
    return num?.toString()?.replace(/[^.0-9]/g, '');
};
export const numberWithCommas = (x: any) => x?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// True when the whole-number part of a currency/amount value has more than `maxDigits` digits.
export const exceedsDigitLimit = (value: unknown, maxDigits = 15): boolean => {
    if (value === null || value === undefined || value === '') return false;

    if (typeof value === 'number') {
        // Compare by magnitude so Infinity and very large values are rejected without float
        // rounding at the boundary or exponential stringification (NaN falls through to false).
        return Math.abs(Math.trunc(value)) >= 10 ** maxDigits;
    }

    // Count the whole-number digits straight from the raw string, ignoring sign, separators,
    // the fractional part and leading zeros.
    const integerDigits = String(value)
        .trim()
        .split('.')[0]
        .replace(/[^0-9]/g, '')
        .replace(/^0+/, '');
    return integerDigits.length > maxDigits;
};

export const isEqual = (obj1: Record<string, any>, obj2: Record<string, any>): boolean => {
    const normalize = (obj: Record<string, any>): [string, any][] => {
        return Object.entries(obj)
            .sort(([keyA], [keyB]) => keyA.localeCompare(keyB)) // Sort by key
            .map(([key, value]) => [key, value]);
    };

    const normalizedObj1 = normalize(obj1);
    const normalizedObj2 = normalize(obj2);

    return JSON.stringify(normalizedObj1) === JSON.stringify(normalizedObj2);
};

export const sortObjectKeysAlphabetically = (obj: any) => {
    // Get the keys of the object and sort them alphabetically
    const sortedKeys = Object.keys(obj).sort();

    // Create a new object with keys in sorted order
    const sortedObj: any = {}; // Use `any` to allow dynamic key-value assignment

    sortedKeys.forEach((key) => {
        sortedObj[key as keyof any] = obj[key as keyof any]; // Type casting to ensure proper key-value types
    });

    return sortedObj;
};

export const getTenantLabels = (t: any) => ({
    trial: t('TRIAL'),
    fullAccess: t('FULL_ACCESS'),
    demo: t('DEMO')
});

export const Localization = {
    en: 'English-US',
    en_ca: 'English-CA',
    es: 'Spanish-MEX',
    en_mex: 'English-MXN'
};

export const getGoalCountByTerm = (formFields: AdviceGoalPayloadType['formFields'], termTab: TermTabType): number => {
    return formFields.filter(
        (goal: AdviceGoalPayloadType['formFields'][0]) => termTab === goal.data.goalCategoryOrder - 1
    ).length;
};

export const sortGoalsByPriorityOrder = <T extends { goalId?: string }>(goals: T[], runPipeResponses?: any[]): T[] => {
    if (!goals?.length || !runPipeResponses?.length) return goals;

    const orderById = new Map<string, number>();
    runPipeResponses.forEach((res: any) => {
        if (res?.goalId != null && res?.goalPriorityOrder != null) {
            orderById.set(res.goalId, Number(res.goalPriorityOrder));
        }
    });
    if (orderById.size === 0) return goals;

    // Goals without a priority order fall back to Infinity → pushed to the end.
    const getOrder = (g: T) => (g.goalId != null ? orderById.get(g.goalId) : undefined) ?? Infinity;
    return [...goals].sort((a, b) => getOrder(a) - getOrder(b));
};

export const getGOEHeaders = (orgEmail?: string) => ({
    clientemail: orgEmail,
    version: 4
});

const generateAccountsArray = (
    goalId: string,
    incomeSource: any,
    incomeSources: any,
    investmentSource: any,
    investmentsSources: any
) => {
    let accountsArray = [
        ...incomeSource?.flatMap((account: any) => {
            const accountDetails: any = incomeSources?.find(
                (ac: any) => ac.accountId === account.accountNumber && account.isSelected
            );
            if (!accountDetails) {
                return []; // might have been deleted
            }

            return {
                fundingType: 'incomeSources',
                recurringContribution: Number(accountDetails?.amount) || 0,
                escalationPercentage: Number(accountDetails?.escalateIncome) || 0,
                escalationYears: 1,
                frequency: accountDetails?.frequency === 'Monthly' ? 'monthly' : 'yearly',
                startDate: accountDetails?.beginningYear, // since only beginningYear is captured from UI
                endDate: accountDetails?.endYear ? accountDetails?.endYear : Number(accountDetails?.beginningYear) + 5 // if end year is not given by user, using beginningYear + 5
            };
        })
    ];
    accountsArray = [
        ...accountsArray,
        ...investmentSource?.flatMap((account: any) => {
            const accountDetails: InvestmentSourcesType = investmentsSources?.find(
                (ac: InvestmentSourcesType) => ac.accountId === account.accountNumber && account.isSelected
            );
            if (!accountDetails) {
                return []; // might have been deleted
            }
            return {
                fundingType: 'investmentAccounts',
                amount: accountDetails?.amount,
                recurringContribution: Number(accountDetails?.recurringContributions) || 0,
                escalationPercentage: Number(accountDetails?.escalationContributions) || 0,
                escalationYears: 1,
                ...(accountDetails?.oneTimeFutureAmount !== undefined && {
                    oneTimeFutureAmount: Number(accountDetails.oneTimeFutureAmount)
                }),
                startDate: accountDetails?.recurringContributionsStartDate,
                endDate: accountDetails?.recurringContributionsEndDate,
                fundingYear: accountDetails?.fundingYear,
                frequency: accountDetails?.frequency === 'Monthly' ? 'monthly' : 'yearly',
                ...(account?.isSelected && { includeRecurringContributionsForGoal: goalId })
            };
        })
    ];
    return accountsArray;
};

// disable IWS AEX-1611
// const prepareIWSData = () => {
//     //payload for wealth goals
//     return {
//         riskProfile: adviceClient?.riskProfile,
//         goals: adviceGoals?.formFields?.map((goal: any) => {
//             return {
//                 goalId: goal?.goalId,
//                 goalType: iwsGoalTypeMapper[goal?.data?.goalType],
//                 goalName: goal?.data?.goalName || '',
//                 riskProfile: adviceClient?.riskProfile,
//                 targetDate: goal?.data?.goalDate || goal?.data?.targetDate,
//                 goalAmount: goal?.data?.goalAmount,
//                 targetedRetirementIncome: goal?.data?.targetedRetirementIncome,
//                 planStartRetirement: goal?.data?.planStartRetirement,
//                 goalPriority: goal?.data?.goalPriority,
//                 accounts: generateAccountsArray(
//                     goal?.goalId,
//                     goal?.data?.sources?.incomeSource,
//                     adviceClient?.accounts?.incomeSources,
//                     goal?.data?.sources?.investmentSource,
//                     adviceClient?.accounts?.investmentsSources
//                 )
//             };
//         })//     };
// };

// Get retirement age and dob based on plan start retirement date ref: AEX-2292
const getRetAgeAndDOBbasedOnPlanStartRetirement = (goal: any, clientData: any) => {
    // Get co-client relationship data
    const coClientRelationship = clientData?.relationship?.[0];
    const planStartRetirementKey = goal?.data?.planStartRetirementKey;
    const coClientRetirementAge = coClientRelationship?.retirementAge || DEFAULT_RETIREMENT_AGE;
    // Check if plan start retirement matches co-client's calculated retirement date
    const isCoClientRetirement =
        coClientRelationship &&
        (planStartRetirementKey === TargetDateKeys.coClient ||
            (!planStartRetirementKey &&
                goal?.data?.planStartRetirement === addAgeToDate(coClientRelationship?.age, coClientRetirementAge)));

    return isCoClientRetirement
        ? {
              retirementAge: +coClientRetirementAge,
              dob: coClientRelationship?.age
          }
        : {
              retirementAge: +clientData?.retirementAge || DEFAULT_RETIREMENT_AGE,
              dob: clientData?.clientDob
          };
};

const prepareRunpipeData = (goal: any, clientData?: any) => ({
    isIWSApplied: false,
    goalId: goal.goalId,
    goalType: iwsGoalTypeMapper[goal?.data?.goalType],
    goalName: goal?.data?.goalName || '',
    riskProfile: getRiskProfile(clientData?.riskProfile),
    targetDate: goal?.data?.goalDate || goal?.data?.targetDate,
    ...(goal?.data?.goalAmount !== undefined && { goalAmount: goal.data.goalAmount }),
    goalPriority: goal?.data?.goalPriority,
    ...(goal?.data?.planStartRetirement && {
        planStartRetirement: goal.data.planStartRetirement
    }),
    ...(goal?.data?.targetedRetirementIncome !== undefined && {
        targetedRetirementIncome: goal.data.targetedRetirementIncome
    }),
    ...getRetAgeAndDOBbasedOnPlanStartRetirement(goal, clientData),
    accounts: generateAccountsArray(
        goal?.goalId,
        goal?.data?.sources?.incomeSource,
        clientData?.accounts?.incomeSources,
        goal?.data?.sources?.investmentSource,
        clientData?.accounts?.investmentsSources
    )
});

export const callRunpipe: CallRunpipePropsType = async (
    realloc_schedules,
    orgEmail,
    adviceClient,
    adviceGoals,
    dispatch,
    goalData?,
    clientData?
) => {
    try {
        let enablePlanDetails = goalData?.global?.enablePlanDetails ?? false;

        //setting default to false untill saveplan is hit
        goalData = goalData || adviceGoals;
        clientData = clientData || adviceClient;
        // IWS api call
        // const iwsResponse = !onlyRunpipe ? await iws(prepareIWSData(), getGOEHeaders(global?.orgInfo?.orgEmail)) : { goalResponseList: [] };
        let requestArrayForRedux: any = [];
        let requestArrayForReduxGoalAmt: any = [];
        let responseArrayForRedux: any = [];
        let responseArrayForReduxGoalAmt: any = [];
        let keyData: any = [];
        let newArr: any = [];
        let newArrGoalAmt: any = [];
        let recommendationArr: any = [];
        // parallel runpipe and goal calculator api calls for each goal
        goalData?.formFields?.map(async (goal: any) => {
            const runpipeRequest = prepareRunpipeData(goal, clientData);
            const goalCalculatorRequest = cloneDeep(runpipeRequest);
            // adding realloc_schedules to runpipe payload ref: 1831
            runpipeRequest['realloc_schedules'] = realloc_schedules;
            if (goalCalculatorRequest.goalType !== 'retirement') goalCalculatorRequest['goalAmount'] = 0;

            requestArrayForRedux.push(runpipeRequest);
            requestArrayForReduxGoalAmt.push(goalCalculatorRequest);
            const newPrRunpipe = Promise.allSettled([runpipe(runpipeRequest, getGOEHeaders(orgEmail))]);
            responseArrayForRedux.push(newPrRunpipe);
            const newPrGoalAmt = Promise.allSettled([goalcalculator(goalCalculatorRequest, getGOEHeaders(orgEmail))]);
            responseArrayForReduxGoalAmt.push(newPrGoalAmt);
            keyData.push(goal.goalId);
            recommendationArr.push({
                goalId: runpipeRequest.goalId,
                orginalAmount: runpipeRequest.goalAmount,
                oneTimeTopUp: 0,
                topUpAccumulation: 0,
                topUpDecumulation: 0,
                recomendedTenure: 0
            });
        });

        const resPr: any = await Promise.allSettled(responseArrayForRedux);
        // eslint-disable-next-line no-unused-vars
        resPr.forEach((_: any, index: string | number) => {
            if (resPr[index]['value'][0]?.value) {
                resPr[index]['value'][0]['value']['goalId'] = keyData[index];
                newArr.push(resPr[index].value[0]?.value);
            } else {
                newArr.push({ goalId: keyData[index] });
            }
        });
        const resPrGoalAmt: any = await Promise.allSettled(responseArrayForReduxGoalAmt);
        // eslint-disable-next-line no-unused-vars
        resPrGoalAmt.forEach((_: any, index: string | number) => {
            if (resPrGoalAmt[index]['value'][0]?.value) {
                resPrGoalAmt[index]['value'][0]['value']['goalId'] = keyData[index];
                newArrGoalAmt.push(resPrGoalAmt[index].value[0]?.value);
            } else {
                newArrGoalAmt.push({ goalId: keyData[index] });
            }
        });
        await dispatch(
            saveGoal({
                ...goalData,
                request: requestArrayForRedux,
                response: newArr,
                recommendation: recommendationArr,
                global: {
                    ...goalData?.global,
                    hasRunPipe: true,
                    banner: {
                        isGoalSummary: false,
                        type: 'success',
                        visible: !(goalData?.global?.banner?.visible === 'new')
                    },
                    responseGoalCalculator: newArrGoalAmt,
                    isPlanRun: true,
                    enablePlanDetails: enablePlanDetails
                }
            })
        );
    } catch (error) {
        console.error(error);
    }
};

export const checkFFEnabled = (fflag: string | string[]) => {
    const currentState = store.getState();
    return Array.isArray(fflag)
        ? fflag.every((ff) => currentState.global.featureFlags?.includes(ff))
        : currentState.global.featureFlags?.includes(fflag);
};

// AEX-3363: a save/update rejected because the plan was changed in another tab. The backend
// (flag AEX-3342_add_validation_before_update) returns a 400 ValidationError whose message
// contains "stale data detected" for both client_ref_id and proposal_ref_id mismatches. Gated on
// the same flag so the "Plan Has Changed" banner only appears when the backend validation is live.
export const isStalePlanError = (error: unknown): boolean => {
    if (!checkFFEnabled(FEATURE_NAMES['AEX-3342_ADD_VALIDATION_BEFORE_UPDATE'])) return false;
    const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
    return typeof message === 'string' && message.toLowerCase().includes('stale data detected');
};

export const isMonthYearDatesEnabled = () => {
    if (!checkFFEnabled(FEATURE_NAMES['DATEFIELD_MMYYYY_FE_AEX-3223'])) {
        return false;
    }
    // Scope the MM/YYYY date behavior to the flow2 tenant (banortemex) only.
    // Non-flow2 tenants keep the previous date behavior.
    const tenant = store.getState().adviceClient?.tenant;
    return getClientJourneyFlowType(tenant) === 'flow2';
};

/**
 * When BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021 FF is enabled,
 * strips accountId, createdAt, updatedAt from all elements of both
 * incomeSources and investmentsSources arrays, and adds update flag.
 * Returns the payload unchanged when FF is disabled.
 */
export const applyAccountFieldStripping = (payload: any, isPlanRun?: boolean | undefined) => {
    if (!checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])) {
        return {
            ...payload,
            accounts: {
                ...payload.accounts,
                incomeSources: (payload.accounts?.incomeSources || []).map(
                    // eslint-disable-next-line no-unused-vars
                    ({ update, isNew, ...rest }: any) => rest
                ),
                investmentsSources: (payload.accounts?.investmentsSources || []).map(
                    // eslint-disable-next-line no-unused-vars
                    ({ update, isNew, ...rest }: any) => rest
                )
            }
        };
    }
    return {
        ...payload,
        accounts: {
            ...payload.accounts,
            incomeSources: (payload.accounts?.incomeSources || []).map(
                // eslint-disable-next-line no-unused-vars
                ({ updatedAt, createdAt, update, ...rest }: any) => {
                    // Newly created (FE-generated) sources must not carry an `update` flag.
                    // Preserve `isNew` so the marker survives in redux until /save_proposal clears it.
                    if (!rest.accountId || rest.isNew) return { ...rest };
                    if (!isPlanRun) return { ...rest, update: false };
                    return update !== undefined ? { ...rest, update: !!update } : { ...rest };
                }
            ),
            investmentsSources: (payload.accounts?.investmentsSources || []).map(
                // eslint-disable-next-line no-unused-vars
                ({ updatedAt, createdAt, update, ...rest }: any) => {
                    // Newly created (FE-generated) sources must not carry an `update` flag.
                    // Preserve `isNew` so the marker survives in redux until /save_proposal clears it.
                    if (!rest.accountId || rest.isNew) return { ...rest };
                    if (!isPlanRun) return { ...rest, update: false };
                    return update !== undefined ? { ...rest, update: !!update } : { ...rest };
                }
            )
        }
    };
};

// utils function to sort data by date in descending order,
// where dateKey is the key in the object that contains the date
export function sortByDateDescendingOrder<T>(data: T[], dateKey: keyof T): T[] {
    return [...data].sort((a, b) => new Date(String(b[dateKey])).getTime() - new Date(String(a[dateKey])).getTime());
}

//utils function to download pdf through backend
export type PdfDownloadToastState = {
    open: boolean;
    generating: boolean;
    type?: 'confirm' | 'error';
};

// eslint-disable-next-line no-unused-vars
export type SetPdfToast = (value: PdfDownloadToastState) => void;

const extractBlobErrorMessage = async (blob: Blob): Promise<string | undefined> => {
    try {
        const text = await blob.text();
        return JSON.parse(text)?.message;
    } catch {
        return undefined;
    }
};

/**
 * Downloads the proposal PDF via backend.
 *
 * When `setToast` is provided it is called with `{ type: 'confirm' }` on success
 * or `{ type: 'error' }` on failure. The function never throws — callers can
 * rely on the toast contract to surface the outcome.
 */
//create interface for downloadpdfthroughbackend params
export interface DownloadPdfParams {
    tenant: string;
    proposalId: string;
    assignedClientId: string;
    da_advice_id: string;
    orgEmail: string;
    weightType: string;
    filename: string;
    language: string;
}

export const downloadPdfThroughBackend = async (
    { tenant, proposalId, assignedClientId, da_advice_id, orgEmail, weightType, filename, language }: DownloadPdfParams,
    setToast?: SetPdfToast
) => {
    try {
        let payload: {
            tenant: string;
            proposalId: string;
            assignedClientId: string;
            da_advice_id: string;
            orgEmail: string;
            weight_type: string;
            language: string;
            playwright?: boolean;
        } = {
            tenant,
            proposalId: proposalId,
            assignedClientId: assignedClientId || '',
            da_advice_id: da_advice_id,
            orgEmail,
            weight_type: weightType,
            language
        };
        if (checkFFEnabled(FEATURE_NAMES['PDF_OPTIMIZATION_AEX-3362'])) {
            payload = {
                ...payload,
                playwright: true
            };
        }
        const response = await uipApiInstance({
            method: 'POST',
            url: '/api/advice/downloadPDF',
            withCredentials: false,
            data: payload,
            responseType: 'blob'
        });

        const blob: Blob | undefined = response?.data;
        if (blob?.type !== 'application/pdf') {
            const message = blob ? await extractBlobErrorMessage(blob) : undefined;
            throw new Error(message || 'Failed to download PDF');
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
        setToast?.({ open: true, generating: false, type: 'confirm' });
    } catch (error) {
        console.error('Failed to download PDF:', error);
        setToast?.({ open: true, generating: false, type: 'error' });
    }
};

/**
 * Downloads a proposal file from the document store.
 *
 * Payload contains tenant and filePath to locate the file in the store.
 * When `setToast` is provided it is called with `{ type: 'confirm' }` on success
 * or `{ type: 'error' }` on failure. The function never throws.
 */
export const getFileFromStore = async (
    tenant: string | undefined,
    filePath: string | undefined,
    filename: string,
    setToast?: SetPdfToast
) => {
    try {
        const data: Record<string, string | undefined> = {
            from_tenant: tenant,
            filePath
        };

        const response = await uipApiInstance({
            method: 'POST',
            url: '/api/advice/getfilefromstore',
            withCredentials: false,
            data,
            responseType: 'blob'
        });

        const blob: Blob | undefined = response?.data;
        if (blob?.type !== 'application/pdf') {
            const message = blob ? await extractBlobErrorMessage(blob) : undefined;
            throw new Error(message || 'Failed to download file');
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);

        setToast?.({ open: true, generating: false, type: 'confirm' });
    } catch (error) {
        console.error('Failed to download file:', error);
        setToast?.({ open: true, generating: false, type: 'error' });
    }
};

export const buildAnnualContribution = (
    goePayload: any,
    goalId: string | undefined,
    infusionIndexMap: Record<string, number> = {},
    isRecommendationApplied: boolean
): AnnualContributionDetails => {
    if (!goePayload) {
        return { amount: 0 };
    }

    const cashflowDt = dayjs(goePayload?.cashflowDate, 'DD-MM-YYYY');
    const startDt = dayjs(goePayload?.currDate, 'DD-MM-YYYY');
    const endDt = dayjs(goePayload?.endDate, 'DD-MM-YYYY');
    const yrdiff = endDt.diff(startDt, 'year') + 1;

    const infusionsArray: { infusion: number; realisationDt: dayjs.Dayjs }[] = [];

    goePayload?.infusions?.forEach((infusion: number, index: number) => {
        if (index === goePayload?.infusions?.length - 1) {
            infusionsArray.push({ infusion, realisationDt: endDt });
            return;
        }

        if (yrdiff < goePayload?.infusions?.length && !cashflowDt.isSame(startDt)) {
            if (index === 0) {
                infusionsArray.push({ infusion, realisationDt: startDt });
                return;
            }
            infusionsArray.push({ infusion, realisationDt: cashflowDt.add(index - 1, 'year') });
            return;
        }

        infusionsArray.push({ infusion, realisationDt: cashflowDt.add(index, 'year') });
    });

    const baseline: AnnualContributionDetails = {
        amount: 0,
        realisationDt: startDt.isValid() ? startDt.format('DD-MM-YYYY') : undefined,
        isFinalYear: infusionsArray.length <= 2
    };

    if (
        isRecommendationApplied &&
        goalId &&
        typeof infusionIndexMap[goalId] === 'number' &&
        infusionIndexMap[goalId] !== 0 &&
        infusionsArray.length
    ) {
        const lastIndex = Math.min(infusionIndexMap[goalId], infusionsArray.length - 1);
        const infusionData = infusionsArray[lastIndex];
        return {
            amount: infusionData?.infusion ?? 0,
            realisationDt: infusionData?.realisationDt?.format('DD-MM-YYYY'),
            isFinalYear: false
        };
    }

    const positiveInfusion = infusionsArray.find((item) => item.infusion > 0);

    if (positiveInfusion) {
        return {
            amount: positiveInfusion.infusion,
            realisationDt: positiveInfusion.realisationDt?.format('DD-MM-YYYY'),
            isFinalYear: false
        };
    }

    return baseline;
};

export const calculateAmountAfterMonths = (startDateInput: string, endDateInput: string): number => {
    const parseDate = (dateInput: string): Date => {
        const [month, day, year] = dateInput.split('-').map(Number);
        return new Date(year, month - 1, day);
    };

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    const startDate = parseDate(startDateInput);
    const endDate = parseDate(endDateInput);

    const startYear = startDate.getFullYear();
    const startMonth = startDate.getMonth() + 1;
    const endYear = endDate.getFullYear();
    const endMonth = endDate.getMonth() + 1;

    if (currentYear < startYear || currentYear > endYear) {
        return 0;
    }

    if (currentYear === startYear && currentYear === endYear) {
        return endMonth - startMonth + 1;
    }

    if (currentYear === startYear) {
        return 12 - (startMonth - 1);
    }

    if (currentYear === endYear) {
        if (currentMonth <= endMonth) {
            return endMonth;
        }
        return 0;
    }

    return 12;
};

export const calculateInitialAmounts = (
    accounts: any[] = [],
    sources: any = {}
): { initialInvestment: number; annualContribution: number } => {
    const { incomeSource = [], investmentSource = [] } = sources || {};

    const sumIncomeSource = sumBy(
        incomeSource,
        ({ isSelected, accountBalance, beginningYear = '01-01-2025', endYear = '01-01-2025', frequency }: any) => {
            if (!isSelected) {
                return 0;
            }

            const [beginDay, beginMonth, beginYear] = beginningYear.split('-');
            const [endDay, endMonth, endYearPart] = endYear.split('-');
            const formattedBeginning = `${beginMonth}-${beginDay}-${beginYear}`;
            const formattedEnd = `${endMonth}-${endDay}-${endYearPart}`;
            const months = calculateAmountAfterMonths(formattedBeginning, formattedEnd);

            if (frequency === 'Annually') {
                return months * accountBalance;
            }

            return months * (accountBalance / 12);
        }
    );

    const sumInvestmentSource = sumBy(
        investmentSource,
        ({
            isRCSelected,
            recurringContributions,
            recurringContributionsStartDate = '01-01-2025',
            recurringContributionsEndDate = '01-01-2025'
        }: any) => {
            if (!isRCSelected) {
                return 0;
            }

            const months = calculateAmountAfterMonths(recurringContributionsStartDate, recurringContributionsEndDate);
            return months * recurringContributions;
        }
    );

    const initialInvestment = sumBy(accounts, 'amount') || 0;
    const annualContribution = sumIncomeSource + sumInvestmentSource;

    return { initialInvestment, annualContribution };
};

export const extractComposition = (compositionSource: any): CompositionEntry[] => {
    if (!compositionSource) {
        return [];
    }

    if (Array.isArray(compositionSource)) {
        return compositionSource
            .filter((item) => item && (item.label || item.name))
            .map((item) => ({
                label: item.label || item.name,
                value: `${Math.round(Number(item?.value ?? item?.percentage ?? 0))}%`
            }));
    }

    if (typeof compositionSource === 'object') {
        return Object.entries(compositionSource).map(([label, value]) => ({
            label,
            value: `${Math.round(Number(value) * 100)}%`
        }));
    }

    return [];
};

// eslint-disable-next-line no-unused-vars
export const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            func(...args);
        }, delay);
    };
};

export const getPriorityColor = (theme: any, priority?: string) => {
    if (!priority) return 'default';
    const lowerPriority = priority.toLowerCase();
    if (lowerPriority.includes('need')) return theme.palette.semantic.bg.info; // bg/info
    if (lowerPriority.includes('want')) return theme.palette.semantic.bg.brandLight2; // bg/brandLight2
    if (lowerPriority.includes('aspire')) return theme.palette.semantic.bg.warning1; // bg/warning1
    if (lowerPriority.includes('wish')) return theme.palette.semantic.bg.error; // bg/error
    if (lowerPriority.includes('dream')) return theme.palette.semantic.bg.warning1; // bg/warning1
    if (lowerPriority.includes('desire')) return theme.palette.semantic.bg.error; // bg/error
    if (lowerPriority.includes('ontrack')) return theme.palette.semantic.bg.success1; // bg/success1
    if (lowerPriority.includes('offtrack')) return theme.palette.semantic.int.negative; // int/negative
    if (lowerPriority.includes('atrisk')) return theme.palette.semantic.bg.warning2; // bg/warning2
    return 'default';
};

export const getDurationColor = (theme: any, dur?: string) => {
    if (!dur) return 'default';
    const lowerDur = dur.toLowerCase();
    if (lowerDur.includes('short')) return theme.palette.semantic.bg.info; // bg/info
    if (lowerDur.includes('medium')) return theme.palette.semantic.bg.success1; // bg/success1
    if (lowerDur.includes('long')) return theme.palette.semantic.bg.warning2; // bg/warning2
    return 'default';
};

export const getDurationLabelFromAccountType = (
    accountType: string,
    accountsTypeArray: InvestmentFundOptions[]
): string => {
    return accountsTypeArray?.find((acct: InvestmentFundOptions) => acct.value === accountType)?.duration || 'N/A';
};

export const getLabelFromAccountType = (accountType: string, accountsTypeArray: InvestmentFundOptions[]): string => {
    return accountsTypeArray?.find((acct: InvestmentFundOptions) => acct.value === accountType)?.label || 'N/A';
};

export const priorityOptionsFlow = (t: any, goalPriorityEnum?: Record<string, string>) => {
    const enumToUse = goalPriorityEnum || Flow2GoalPriorityEnum;
    return Object.keys(enumToUse).map((key, index) => {
        const enumValue = enumToUse[key as keyof typeof enumToUse];
        const translationKey = `${enumValue.toUpperCase()}`;
        return {
            label: t(translationKey),
            value: enumValue,
            index: index + 1
        };
    });
};

const toFrequency = (freq: string) => (freq === 'Annually' ? 'yearly' : 'monthly');

// AEX-3108: single-portfolio tenants have no Short/Medium/Long terms, so they plan against one `default` term.
const isMultiPortfolioTenant = (): boolean => Boolean(store.getState()?.global?.globalConfig?.isMultiPortfolioTenant);
const getTenantEwsDurations = (): readonly EwsDuration[] => getEwsDurations(isMultiPortfolioTenant());
// AEX-3108: multiportfolio tenants send their term's bundle name; single-portfolio tenants send 'default'.
export const resolvePortfolioSetLabel = (
    mappings: goalCategoryPortfolioMappingsType | undefined,
    category: string | undefined
): string | undefined =>
    isMultiPortfolioTenant() ? mappings?.[category as string]?.portfolioBundleName : DEFAULT_GOAL_CATEGORY;

// Plans saved/flagged before AEX-3108 may carry Short/Medium/Long keys; a single-portfolio tenant only has `default`.
const normalizeAffectedTerms = (terms: EwsDurationKey[] | undefined): EwsDurationKey[] | undefined =>
    !isMultiPortfolioTenant() && terms?.length ? [DEFAULT_GOAL_CATEGORY] : terms;

// A single-portfolio tenant's goals all belong to the default term, whatever goalCategory a saved goal carries.
const goalMatchesDuration = (goal: any, duration: EwsDuration): boolean =>
    duration === DEFAULT_GOAL_CATEGORY || goal?.data?.goalCategory === duration;
export const getGoalDurationKey = (goal: any): EwsDurationKey =>
    isMultiPortfolioTenant() ? EWS_DURATION_KEYS[goal?.data?.goalCategory as EwsDuration] : DEFAULT_GOAL_CATEGORY;

export const getAccountsByDuration = (
    adviceClient: any,
    incomeSourceTypes: any,
    investmentAccountTypes: any,
    duration: EwsDuration
) => {
    const incomeSources = (adviceClient?.accounts?.incomeSources ?? [])
        .filter((src: any) =>
            incomeSourceTypes?.some(
                (t: any) =>
                    t?.value === src?.sourceType && (duration === DEFAULT_GOAL_CATEGORY || t?.duration === duration)
            )
        )
        .map((src: any) => ({
            accountId: src?.accountId,
            amount: 0,
            infusionType: toFrequency(src?.frequency),
            startDate: src?.beginningYear,
            endDate: src?.endYear,
            fundingType: 'incomeSources',
            recurringContribution: src?.amount,
            fundingSourceType: src?.sourceType
        }));

    const investmentSources = (adviceClient?.accounts?.investmentsSources ?? [])
        .filter((src: any) =>
            investmentAccountTypes?.some(
                (t: any) =>
                    t?.value === src?.accountType && (duration === DEFAULT_GOAL_CATEGORY || t?.duration === duration)
            )
        )
        .map((src: any) => ({
            accountId: src?.accountId,
            amount: src?.amount,
            infusionType: toFrequency(src?.frequency),
            startDate: src?.recurringContributionsStartDate || null,
            endDate: src?.recurringContributionsEndDate || null,
            fundingType: 'investmentAccounts',
            recurringContribution: src?.recurringContributions || 0,
            oneTimeFutureAmount: src?.oneTimeFutureAmount || 0,
            fundingYear: src?.fundingYear || null,
            fundingSourceType: src?.accountType
        }));

    return [...incomeSources, ...investmentSources];
};

/**
 * Returns the list of goal tenures (EwsDuration) for which goals exist but
 * no relevant funding sources (income sources or investment accounts) are available.
 */
export const getMissingFundingTenures = (
    adviceClient: any,
    adviceGoals: any,
    incomeSourceTypes: any,
    investmentAccountTypes: any
): EwsDuration[] => {
    const formFields = adviceGoals?.formFields ?? [];
    return getTenantEwsDurations().filter((duration) => {
        const hasGoalsForTenure = formFields.some((g: any) => goalMatchesDuration(g, duration));
        if (!hasGoalsForTenure) return false;
        const accounts = getAccountsByDuration(adviceClient, incomeSourceTypes, investmentAccountTypes, duration);
        return accounts.length === 0;
    });
};

/**
 * Returns the subset of `adviceGoals.formFields` whose tenure (goalCategory) has at least
 * one eligible funding account. Used to filter goals at read time for EWS payloads,
 * Plan Overview, Plan Execution and probability calculations without mutating redux state.
 */
export const getEligibleGoals = (
    adviceClient: any,
    adviceGoals: any,
    incomeSourceTypes: any,
    investmentAccountTypes: any
): any[] => {
    const formFields = adviceGoals?.formFields ?? [];
    if (formFields.length === 0) return [];
    const tenureFundingCache = new Map<string, boolean>();
    const hasFundingForTenure = (tenure: EwsDuration): boolean => {
        if (!tenure) return false;
        if (tenureFundingCache.has(tenure)) return tenureFundingCache.get(tenure) as boolean;
        const hasFunding =
            getAccountsByDuration(adviceClient, incomeSourceTypes, investmentAccountTypes, tenure).length > 0;
        tenureFundingCache.set(tenure, hasFunding);
        return hasFunding;
    };
    const isMultiPortfolio = isMultiPortfolioTenant();
    return formFields.filter((g: any) =>
        hasFundingForTenure(isMultiPortfolio ? (g?.data?.goalCategory as EwsDuration) : DEFAULT_GOAL_CATEGORY)
    );
};

/**
 * True when any goal in any EWS duration returned `noEligibleFundings` from runpipe. This is a
 * valid terminal state (not a retryable failure), but must block advancing past the Funding
 * Navigator since an unfunded goal skews Plan Overview and probability calculations.
 */
export const hasNoEligibleFundingGoals = (): boolean => {
    const ewsResponses = getEwsResponsesState();
    return Object.values(ewsResponses).some((ewsResponse: any) =>
        ewsResponse?.response?.body?.runPipeResponses?.some((run: any) => run?.noEligibleFundings)
    );
};

const getGoalProfileList = (
    adviceGoals: any,
    category: EwsDuration,
    accounts: any[],
    goalCategoryPortfolioMappings: goalCategoryPortfolioMappingsType | undefined,
    reallocSchedules?: CallRunpipeArgs['realloc_schedules'],
    // AEX-3270: when the account↔income mapping FF is on the per-goal `accounts` array is
    // dropped from goalProfileList (account linkage is expressed via `accountFundingLinks`).
    includeAccountsField = true
) => {
    const accountIds = accounts.map((acc: any) => ({ accountId: acc?.accountId }));
    // AEX-3603: a plan never saved yet is first-time onboarding, so continuity uses the baseline.
    const isPlanSavedOnce = Boolean(adviceGoals?.global?.isPlanSavedOnce);
    const { reallocate = false } = buildRootContinuityFields(isPlanSavedOnce);
    return (adviceGoals?.formFields ?? [])
        .filter((goal: any) => goalMatchesDuration(goal, category))
        .map((goal: any) => {
            const isRetirement = goal?.data?.type === iwsGoalTypeMapper.retirement;
            const isIncome = goal?.data?.type === iwsGoalTypeMapper.income;
            const portfolioSetLabel = resolvePortfolioSetLabel(goalCategoryPortfolioMappings, category);
            let goalProfile: any = {
                goalId: goal.goalId,
                goalName: goal?.data?.goalName,
                goalAmount: isRetirement ? goal?.data?.targetedRetirementIncome : goal?.data?.goalAmount,
                bequestAmount: isRetirement ? goal?.data?.goalAmount : 0,
                goalType: isIncome || isRetirement ? 'retirement' : 'regular', // scenarioType
                goalPriority: goal?.data?.goalPriority,
                targetDate: goal?.data?.targetDate,
                ...(isRetirement && { planStartRetirement: goal?.data?.planStartRetirement }),
                ...(isIncome && { planStartRetirement: goal?.data?.incomeStartDate }),
                goalFrequency: toFrequency(goal?.data?.frequency),
                type: goal?.data?.type, // like income/wealth
                ...(includeAccountsField && { accounts: accountIds }),
                reallocSchedules,
                ...buildGoalContinuityFields(goal, isPlanSavedOnce, reallocate)
            };
            if (portfolioSetLabel) {
                goalProfile = {
                    ...goalProfile,
                    portfolioSetLabel: portfolioSetLabel
                };
            }
            return goalProfile;
        });
};

const extractAndSyncRunpipeRequests = (formFields: any[], adviceClient: any): Record<string, any> => {
    const requests = (formFields ?? []).map((goal: any) => prepareRunpipeData(goal, adviceClient));
    return requests;
};

const getEwsResponsesState = (): Record<string, any> => store.getState().ews?.responses ?? {};
const getEwsRequestsState = (): Record<string, any> => store.getState().ews?.requests ?? {};

// AEX-3603 Continuity of Advice --------------------------------------------------------------
const isAdviceContinuityEnabled = (): boolean => !!checkFFEnabled(FEATURE_NAMES['CONTINUITY_OF_ADVICE_FE_AEX-3603']);

const getAdviceContinuityState = (): AdviceContinuityState =>
    store.getState().ews?.adviceContinuity ?? { reallocate: false, isNewRiskProfile: false, goals: {} };

// A goal absent from the continuity map is treated as first-time onboarding (all change flags true,
// no prior portfolio) — see AEX-3603 baseline row.
const resolveGoalContinuity = (goalId: string): AdviceContinuityGoal =>
    getAdviceContinuityState()?.goals?.[goalId] ?? ONBOARDING_BASELINE_CONTINUITY_GOAL;

// Per-goal continuity keys spread into a goalProfile. `currentPortfolioId` is sourced from the
const buildGoalContinuityFields = (goal: any, isPlanSavedOnce: boolean, reallocate: boolean): Record<string, any> => {
    if (!isAdviceContinuityEnabled()) return {};
    const { isNewGoal, isNewGoalPriority, isNewInvestmentTenure } = isPlanSavedOnce
        ? resolveGoalContinuity(goal?.goalId)
        : ONBOARDING_BASELINE_CONTINUITY_GOAL;
    const lastRecommendedPortfolioId = goal?.data?.lastRecommendedPortfolioId;
    return {
        isNewGoal,
        isNewGoalPriority,
        isNewInvestmentTenure,
        ...(reallocate && lastRecommendedPortfolioId != null && { currentPortfolioId: lastRecommendedPortfolioId })
    };
};

// Before the plan's first save (isPlanSavedOnce=false) the plan is onboarding: reallocate=false,
const buildRootContinuityFields = (isPlanSavedOnce: boolean): Record<string, any> => {
    if (!isAdviceContinuityEnabled()) return {};
    if (!isPlanSavedOnce) return { reallocate: false, isNewRiskProfile: true };
    const { reallocate, isNewRiskProfile } = getAdviceContinuityState();
    return { reallocate, isNewRiskProfile };
};

// Prior recommended portfolio id per goalId, read from the previous advice's EWS responses. Used to
// seed `currentPortfolioId` when a reallocation begins.
export const extractRecommendedPortfolioIdByGoal = (
    ewsResponses: Record<string, any>
): Record<string, string | null> => {
    const map: Record<string, string | null> = {};
    Object.values(ewsResponses ?? {}).forEach((term: any) => {
        (term?.response?.body?.runPipeResponses ?? []).forEach((runPipe: any) => {
            if (runPipe?.goalId) {
                map[runPipe.goalId] = runPipe?.response?.body?.analysisReport?.recommendedPortfolioId ?? null;
            }
        });
    });
    return map;
};

const dispatchMergedEwsState = (
    dispatch: any,
    adviceGoals: any,
    adviceClient: any,
    ewsRequests: Record<string, any>,
    ewsResponses: Record<string, any>
) => {
    const mergedResponses = extractAndSyncRunpipeResponses(adviceGoals?.formFields, ewsResponses);
    const mergedRequests = extractAndSyncRunpipeRequests(adviceGoals?.formFields, adviceClient);

    // Continuity of Advice (flow2): after a successful EWS run, stamp each goal's latest recommended
    // portfolio id onto its formField. Overwrite when EWS returned a value; add the attribute (null)
    // when it is missing on the goal.
    const isFlow2 = getClientJourneyFlowType(adviceClient?.tenant) === 'flow2';
    const stampLastRecommendedPortfolioId =
        checkFFEnabled(FEATURE_NAMES['CONTINUITY_OF_ADVICE_FE_AEX-3603']) && isFlow2;
    const recommendedPortfolioIdByGoal = stampLastRecommendedPortfolioId
        ? extractRecommendedPortfolioIdByGoal(ewsResponses)
        : {};
    const updatedFormFields = (adviceGoals?.formFields ?? []).map((goal: any) => {
        if (!stampLastRecommendedPortfolioId) return goal;
        const hasNewValue = goal?.goalId in recommendedPortfolioIdByGoal;
        const attributeExists = !!goal?.data && 'lastRecommendedPortfolioId' in goal.data;
        if (!hasNewValue && attributeExists) return goal;
        const nextValue = hasNewValue
            ? recommendedPortfolioIdByGoal[goal.goalId]
            : goal?.data?.lastRecommendedPortfolioId ?? null;
        return { ...goal, data: { ...goal.data, lastRecommendedPortfolioId: nextValue } };
    });

    dispatch(setEwsMaps({ requests: { ...ewsRequests }, responses: { ...ewsResponses } }));
    // Keep the aggregate stepper/CTA rollup consistent with the just-merged responses.
    dispatch(setEwsStepperCtaStatus(computeEwsStepperCtaStatus(ewsResponses)));
    dispatch(setAffectedEwsTerms([]));
    dispatch(
        syncAdviceGoals({
            ...adviceGoals,
            formFields: updatedFormFields,
            response: mergedResponses,
            request: mergedRequests,
            global: {
                ...adviceGoals.global,
                isPlanRun: true,
                responseGoalCalculator: [],
                planExecutionResponse: undefined
            }
        })
    );
};

const extractAndSyncRunpipeResponses = (formFields: any[], ewsResponses: Record<string, any>): any[] => {
    const durationKeys = Object.values(EWS_DURATION_KEYS);
    return (formFields ?? []).map((goal: any) => {
        for (const key of durationKeys) {
            const runPipeResponses = ewsResponses[key]?.response?.body?.runPipeResponses;
            const goePayloads = ewsResponses[key]?.goePayload?.runpipe;
            if (!runPipeResponses || runPipeResponses.length === 0) continue;
            const match = runPipeResponses.find((rpr: any) => rpr.goalId === goal.goalId);
            if (!goePayloads || goePayloads.length === 0) continue;
            const goeMatch = goePayloads.find((gp: any) => gp.goalId === goal.goalId);
            if (match && goeMatch) {
                return { goalId: goal.goalId, ...match, goePayload: { ...goeMatch?.runpipe_request } };
            }
        }
        return { goalId: goal.goalId };
    });
};

/**
 * Removes a single deleted goal (by goalId) from the per-term EWS request/response maps while
 * keeping every other goal's data intact. A term whose only remaining goal was the deleted one
 * is dropped entirely from both maps. Pure: returns new maps and never mutates the inputs.
 */
export const pruneGoalFromEwsState = (
    ewsRequests: Record<string, any> = {},
    ewsResponses: Record<string, any> = {},
    goalId: string
): { ewsRequests: Record<string, any>; ewsResponses: Record<string, any> } => {
    const prunedRequests: Record<string, any> = {};
    const prunedResponses: Record<string, any> = {};
    const termKeys = new Set([...Object.keys(ewsRequests ?? {}), ...Object.keys(ewsResponses ?? {})]);

    termKeys.forEach((term) => {
        const request = ewsRequests?.[term];
        const response = ewsResponses?.[term];

        const remainingGoalProfiles = (request?.goalProfileList ?? []).filter((g: any) => g?.goalId !== goalId);
        const remainingRunPipeResponses = (response?.response?.body?.runPipeResponses ?? []).filter(
            (r: any) => r?.goalId !== goalId
        );

        // Once a term no longer references any goal, drop it from both maps entirely.
        if (remainingGoalProfiles.length === 0 && remainingRunPipeResponses.length === 0) return;

        if (request) {
            prunedRequests[term] = { ...request, goalProfileList: remainingGoalProfiles };
        }
        if (response) {
            const body = response?.response?.body;
            prunedResponses[term] = {
                ...response,
                ...(body && {
                    response: {
                        ...response.response,
                        body: {
                            ...body,
                            ...(Array.isArray(body.ews) && {
                                ews: body.ews.filter((e: any) => e?.goalId !== goalId)
                            }),
                            ...(Array.isArray(body.runPipeResponses) && { runPipeResponses: remainingRunPipeResponses })
                        }
                    }
                }),
                ...(Array.isArray(response?.goePayload?.runpipe) && {
                    goePayload: {
                        ...response.goePayload,
                        runpipe: response.goePayload.runpipe.filter((g: any) => g?.goalId !== goalId)
                    }
                })
            };
        }
    });

    return { ewsRequests: prunedRequests, ewsResponses: prunedResponses };
};

// AEX-3452: term is always derived from tenant config (investmentAccountType.duration),
// never the persisted `account.term`, so a config remap takes effect for existing accounts.
const getAccountsForTerm = (adviceClient: any, duration: EwsDuration, investmentAccountTypes: any): any[] =>
    (adviceClient?.accounts?.investmentsSources ?? [])
        .map((account: any) => ({
            ...account,
            term: getDurationLabelFromAccountType(account?.accountType, investmentAccountTypes)
        }))
        .filter((account: any) => duration === DEFAULT_GOAL_CATEGORY || account?.term === duration);

// AEX-3270: maps the term-filtered accounts into the EWS `investmentDetails` array.
const buildInvestmentDetails = (accounts: any[]): any[] =>
    (accounts ?? []).map((account: any) => ({
        accountHolder: account?.accountHolder,
        accountId: account?.accountId,
        accountName: account?.accountName,
        accountType: 'investmentAccounts',
        fundingSourceType: account?.accountType,
        amount: Number(account?.amount) || 0,
        sourceOwner: account?.accountHolder
    }));

// AEX-3270: flattens each account's nested contributions into the EWS `accountFundingLinks`
// array, tagging every link with its parent account's id. Frequency and dates are sent as
// stored (lowercase 'monthly'/'annually', dates in 'DD-MM-YYYY'). For "One Time" (onetime)
// contributions, contributionStartDate and contributionEndDate are sent as the same date
const buildAccountFundingLinks = (accounts: any[]): any[] =>
    (accounts ?? []).flatMap((account: any) =>
        (account?.contributions ?? []).map((contribution: any) => ({
            accountId: account?.accountId,
            incomeSourceId: contribution?.accountId,
            incomeSourceType: contribution?.sourceType,
            sourceOwner: contribution?.sourceOwner,
            contributionAmount: contribution?.amount,
            contributionFrequency: contribution?.frequency,
            contributionStartDate: contribution?.beginningYear,
            contributionEndDate:
                contribution?.frequency === CONTRIBUTION_FREQUENCY.ONE_TIME
                    ? contribution?.beginningYear
                    : contribution?.endYear
        }))
    );

const buildEwsDurationPayload = (
    adviceClient: any,
    incomeSourceTypes: any,
    investmentAccountTypes: any,
    adviceGoals: any,
    duration: EwsDuration,
    tenant: string,
    goalCategoryPortfolioMappings: goalCategoryPortfolioMappingsType | undefined,
    reallocSchedules?: CallRunpipeArgs['realloc_schedules']
) => {
    // AEX-3603: a plan never saved yet is first-time onboarding, so root continuity uses the baseline.
    const isPlanSavedOnce = Boolean(adviceGoals?.global?.isPlanSavedOnce);
    // AEX-3270: account↔income mapping payload. Accounts (filtered by their `term`) become
    // `investmentDetails`; their nested contributions become `accountFundingLinks`. The
    // legacy structure is preserved when the FF is off.
    if (checkFFEnabled(FEATURE_NAMES['ACCOUNT_INCOME_MAPPING_FE_AEX-3270'])) {
        const accounts = getAccountsForTerm(adviceClient, duration, investmentAccountTypes);
        const goalProfileList = getGoalProfileList(
            adviceGoals,
            duration,
            accounts,
            goalCategoryPortfolioMappings,
            reallocSchedules,
            false
        );
        return {
            key: EWS_DURATION_KEYS[duration],
            data: {
                dob: adviceClient?.clientDob,
                retirementAge: Number(adviceClient?.retirementAge),
                riskProfile: getRiskProfile(adviceClient?.riskProfile),
                from_tenant: tenant,
                ...buildRootContinuityFields(isPlanSavedOnce),
                goalProfileList,
                investmentDetails: buildInvestmentDetails(accounts),
                accountFundingLinks: buildAccountFundingLinks(accounts)
            }
        };
    }

    const accounts = getAccountsByDuration(adviceClient, incomeSourceTypes, investmentAccountTypes, duration);
    const goalProfileList = getGoalProfileList(
        adviceGoals,
        duration,
        accounts,
        goalCategoryPortfolioMappings,
        reallocSchedules
    );
    return {
        key: EWS_DURATION_KEYS[duration],
        data: {
            dob: adviceClient?.clientDob,
            retirementAge: Number(adviceClient?.retirementAge),
            riskProfile: getRiskProfile(adviceClient?.riskProfile),
            goalProfileList,
            investmentDetails: accounts,
            from_tenant: tenant,
            ...buildRootContinuityFields(isPlanSavedOnce)
        }
    };
};

const dispatchEwsGoalsSync = (
    dispatch: any,
    adviceGoals: any,
    ewsRequests: Record<string, any>,
    ewsResponses: Record<string, any>,
    extraGlobalFields?: Record<string, any>
) => {
    dispatch(setEwsMaps({ requests: { ...ewsRequests }, responses: { ...ewsResponses } }));
    // Only touch adviceGoals when non-ews global fields must change; keeps stream ticks from
    // replacing the whole adviceGoals object (which would re-render every subscriber).
    if (extraGlobalFields && Object.keys(extraGlobalFields).length > 0) {
        dispatch(
            syncAdviceGoals({
                ...adviceGoals,
                global: {
                    ...adviceGoals.global,
                    ...extraGlobalFields
                }
            })
        );
    }
};

const dispatchGoalCalculatorSync = (dispatch: any, responseGoalCalculator: any[]) => {
    const latestAdviceGoals = store.getState().adviceGoals;
    dispatch(
        syncAdviceGoals({
            ...latestAdviceGoals,
            global: {
                ...latestAdviceGoals?.global,
                responseGoalCalculator: [...responseGoalCalculator]
            }
        })
    );
};

const buildGoalCalculatorPayload = (
    goal: any,
    goePayload: any,
    goalCategoryPortfolioMappings: goalCategoryPortfolioMappingsType | undefined
) => {
    const goalCategory = goal?.data?.goalCategory;
    const portfolioSetLabel = resolvePortfolioSetLabel(goalCategoryPortfolioMappings, goalCategory);
    const requestPayload: Record<string, any> = { ...goePayload };
    if (portfolioSetLabel) {
        requestPayload.portfolioSetLabel = portfolioSetLabel;
    }
    return {
        request_payload: requestPayload,
        goal_id: goal.goalId
    };
};

export const callEWS = async (
    dispatch: any,
    incomeSourceTypes: any,
    investmentAccountTypes: any,
    adviceClient: any,
    adviceGoals: any,
    clientemail: string,
    tenant: string,
    goalCategoryPortfolioMappings: goalCategoryPortfolioMappingsType | undefined,
    reallocSchedules?: CallRunpipeArgs['realloc_schedules']
) => {
    const ewsPayloads = getTenantEwsDurations()
        .map((duration) =>
            buildEwsDurationPayload(
                adviceClient,
                incomeSourceTypes,
                investmentAccountTypes,
                adviceGoals,
                duration,
                tenant,
                goalCategoryPortfolioMappings,
                reallocSchedules
            )
        )
        .filter(({ data }) => data.goalProfileList?.length > 0 && data.investmentDetails?.length > 0);

    const ewsRequests: Record<string, any> = {};
    const ewsResponses: Record<string, any> = {};

    ewsPayloads.forEach(({ key, data }) => {
        ewsRequests[key] = data;
        ewsResponses[key] = { loading: true };
    });

    const callEwsExtraFields = { responseGoalCalculator: [] as any[] };

    // affectedEwsTerms now lives in the ews slice; clear it at the start of an EWS run.
    dispatch(setAffectedEwsTerms([]));

    // Dispatch initial loading state for all 3 duration calls
    dispatchEwsGoalsSync(dispatch, adviceGoals, ewsRequests, ewsResponses, callEwsExtraFields);

    const promises = ewsPayloads.map(({ key, data }) =>
        ews(data, { clientemail })
            .then((response: any) => {
                if (response?.response) {
                    ewsResponses[key] = response;
                } else {
                    ewsResponses[key] = { error: true };
                }
                dispatchEwsGoalsSync(dispatch, adviceGoals, ewsRequests, ewsResponses, callEwsExtraFields);
            })
            .catch((err) => {
                ewsResponses[key] = { error: true, err };
                dispatchEwsGoalsSync(dispatch, adviceGoals, ewsRequests, ewsResponses, callEwsExtraFields);
            })
    );

    await Promise.allSettled(promises);

    dispatchMergedEwsState(dispatch, adviceGoals, adviceClient, ewsRequests, ewsResponses);
    dispatch(setEnableConfirmConsoleNavigation(true));
};

export const getAffectedEwsTerms = (
    existingTerms: EwsDurationKey[] = [],
    newCategory: EwsDuration | string | null,
    oldCategory?: EwsDuration | string | null
): EwsDurationKey[] => {
    const terms = new Set<EwsDurationKey>(existingTerms);

    if (!isMultiPortfolioTenant()) {
        terms.add(DEFAULT_GOAL_CATEGORY);
        return Array.from(terms);
    }

    if (newCategory && EWS_DURATION_KEYS[newCategory as EwsDuration]) {
        terms.add(EWS_DURATION_KEYS[newCategory as EwsDuration]);
    }

    if (oldCategory && EWS_DURATION_KEYS[oldCategory as EwsDuration] && oldCategory !== newCategory) {
        terms.add(EWS_DURATION_KEYS[oldCategory as EwsDuration]);
    }

    return Array.from(terms);
};

export const callSelectiveEWS = async (
    dispatch: any,
    incomeSourceTypes: any,
    investmentAccountTypes: any,
    adviceClient: any,
    adviceGoals: any,
    clientemail: string,
    tenant: string,
    goalCategoryPortfolioMappings: goalCategoryPortfolioMappingsType | undefined,
    reallocSchedules?: CallRunpipeArgs['realloc_schedules']
) => {
    let affectedTerms: EwsDurationKey[] | undefined = normalizeAffectedTerms(store.getState().ews?.affectedEwsTerms);
    const existingEwsResponses = getEwsResponsesState();
    const hasExistingEwsResponses =
        existingEwsResponses &&
        Object.keys(existingEwsResponses).length > 0 &&
        Object.values(existingEwsResponses).every(
            (resp: any) =>
                !resp?.loading &&
                !resp?.error &&
                resp?.response &&
                resp?.response?.body?.runPipeResponses?.length > 0 &&
                resp?.goePayload &&
                resp?.goePayload?.runpipe?.length > 0
        );

    // EWS has never run → run full EWS
    if (!hasExistingEwsResponses) {
        return callEWS(
            dispatch,
            incomeSourceTypes,
            investmentAccountTypes,
            adviceClient,
            adviceGoals,
            clientemail,
            tenant,
            goalCategoryPortfolioMappings,
            reallocSchedules
        );
    }

    // Existing responses present and no goal changes since last run → skip EWS entirely
    if (!affectedTerms?.length) {
        return;
    }
    // if any one of term is in loading state or error state then add that term into affectedEwsTerms
    const allTerms: EwsDurationKey[] = getTenantEwsDurations().map((duration) => EWS_DURATION_KEYS[duration]);
    allTerms.forEach((term) => {
        const response = existingEwsResponses?.[term];
        if (
            !response ||
            response?.loading ||
            response?.error ||
            !response?.response ||
            !response?.response?.body?.runPipeResponses?.length ||
            !response?.goePayload ||
            !response?.goePayload?.runpipe?.length
        ) {
            if (!affectedTerms?.includes(term)) {
                affectedTerms = [...(affectedTerms || []), term];
            }
        }
    });

    // Build payloads only for affected durations
    const affectedDurations = affectedTerms.map((key) => EWS_DURATION_MAP[key]).filter(Boolean);

    const ewsPayloads = affectedDurations.map((duration) =>
        buildEwsDurationPayload(
            adviceClient,
            incomeSourceTypes,
            investmentAccountTypes,
            adviceGoals,
            duration,
            tenant,
            goalCategoryPortfolioMappings,
            reallocSchedules
        )
    );

    // Preserve existing requests/responses for unaffected terms
    const ewsRequests = { ...getEwsRequestsState() };
    const ewsResponses = { ...getEwsResponsesState() };

    // Set loading state for affected terms; clear response for terms with no goals left or no funding sources
    ewsPayloads.forEach(({ key, data }) => {
        ewsRequests[key] = data;
        if (data.goalProfileList?.length > 0 && data.investmentDetails?.length > 0) {
            ewsResponses[key] = { loading: true };
        } else {
            delete ewsRequests[key];
            delete ewsResponses[key];
        }
    });

    const selectiveEwsExtraFields = { responseGoalCalculator: [] as any[] };

    // Dispatch loading state, clear affectedEwsTerms (now in the ews slice)
    dispatch(setAffectedEwsTerms([]));
    dispatchEwsGoalsSync(dispatch, adviceGoals, ewsRequests, ewsResponses, selectiveEwsExtraFields);

    // Only make API calls for terms that have goals AND funding sources
    const payloadsWithGoals = ewsPayloads.filter(
        ({ data }) => data.goalProfileList?.length > 0 && data.investmentDetails?.length > 0
    );

    const promises = payloadsWithGoals.map(({ key, data }) =>
        ews(data, { clientemail })
            .then((response: any) => {
                if (response?.response) {
                    ewsResponses[key] = response;
                } else {
                    ewsResponses[key] = { error: true, err: response };
                }
                dispatchEwsGoalsSync(dispatch, adviceGoals, ewsRequests, ewsResponses, selectiveEwsExtraFields);
            })
            .catch((err) => {
                console.error(err);
                ewsResponses[key] = { error: true, err };
                dispatchEwsGoalsSync(dispatch, adviceGoals, ewsRequests, ewsResponses, selectiveEwsExtraFields);
            })
    );

    await Promise.allSettled(promises);

    dispatchMergedEwsState(dispatch, adviceGoals, adviceClient, ewsRequests, ewsResponses);
    dispatch(setEnableConfirmConsoleNavigation(true));
};

const buildUnifiedEwsPayload = (
    adviceClient: any,
    incomeSourceTypes: any,
    investmentAccountTypes: any,
    adviceGoals: any,
    tenant: string,
    goalCategoryPortfolioMappings: goalCategoryPortfolioMappingsType | undefined,
    affectedTerms: EwsDurationKey[],
    reallocSchedules?: CallRunpipeArgs['realloc_schedules']
): { ewsPayloads: { key: EwsDurationKey; data: any }[]; payload: Record<string, any> } => {
    const ewsPayloads = getTenantEwsDurations()
        .filter((duration) => affectedTerms.includes(EWS_DURATION_KEYS[duration]))
        .map((duration) =>
            buildEwsDurationPayload(
                adviceClient,
                incomeSourceTypes,
                investmentAccountTypes,
                adviceGoals,
                duration,
                tenant,
                goalCategoryPortfolioMappings,
                reallocSchedules
            )
        )
        .filter(({ data }) => data.goalProfileList?.length > 0 && data.investmentDetails?.length > 0);

    const durations: Record<string, any> = {};
    let commonFields: Record<string, any> = {};
    ewsPayloads.forEach(({ key, data }, index) => {
        // out of the per-duration data alongside the shared client fields.
        const { dob, retirementAge, riskProfile, from_tenant, reallocate, isNewRiskProfile, ...durationData } =
            data as Record<string, any>;
        durations[key] = durationData;
        if (index === 0) {
            commonFields = {
                dob,
                retirementAge,
                riskProfile,
                from_tenant,
                ...(reallocate !== undefined && { reallocate }),
                ...(isNewRiskProfile !== undefined && { isNewRiskProfile })
            };
        }
    });

    return { ewsPayloads, payload: { ...commonFields, durations } };
};

export const callUnifiedEWS = async (
    dispatch: any,
    incomeSourceTypes: any,
    investmentAccountTypes: any,
    adviceClient: any,
    adviceGoals: any,
    clientemail: string,
    tenant: string,
    goalCategoryPortfolioMappings: goalCategoryPortfolioMappingsType | undefined,
    reallocSchedules?: CallRunpipeArgs['realloc_schedules']
) => {
    let affectedTerms: EwsDurationKey[] | undefined = normalizeAffectedTerms(store.getState().ews?.affectedEwsTerms);
    // Existing responses present and no goal changes since last run → skip EWS entirely
    if (!affectedTerms?.length) {
        return;
    }

    const { ewsPayloads, payload } = buildUnifiedEwsPayload(
        adviceClient,
        incomeSourceTypes,
        investmentAccountTypes,
        adviceGoals,
        tenant,
        goalCategoryPortfolioMappings,
        affectedTerms,
        reallocSchedules
    );

    // No duration has both goals and funding sources → nothing to request.
    if (!ewsPayloads.length) return;

    const ewsRequests: Record<string, any> = { ...getEwsRequestsState() };
    const ewsResponses: Record<string, any> = { ...getEwsResponsesState() };

    ewsPayloads.forEach(({ key, data }) => {
        ewsRequests[key] = data;
        ewsResponses[key] = { loading: true };
    });

    const unifiedEwsExtraFields = { responseGoalCalculator: [] as any[] };

    // affectedEwsTerms now lives in the ews slice; clear it before the one-shot call.
    dispatch(setAffectedEwsTerms([]));
    // Single loading dispatch for every included term before the one-shot call.
    dispatchEwsGoalsSync(dispatch, adviceGoals, ewsRequests, ewsResponses, unifiedEwsExtraFields);
    // Reset the funding tab to the earliest available term (short > medium > long).
    const firstAvailableTerm = [TermTabType.SHORT, TermTabType.MEDIUM, TermTabType.LONG].find(
        (tab) => ewsRequests[EWS_TERM_TAB_KEY_MAP[tab]]
    );
    if (firstAvailableTerm !== undefined) {
        dispatch(setSelectedFundingTermIndex(firstAvailableTerm));
    }

    if (checkFFEnabled(FEATURE_NAMES['FUNDINGNAV_EWS_STREAMING_FE_AEX-3495'])) {
        // Progressive loading: a single SSE stream returns ews + runpipe chunks for all included terms, merging them into the store as they arrive.
        const completed = await runUnifiedEwsStream(dispatch, payload, clientemail);
        // Aborted (user left the plan or a newer run superseded it): adviceGoals here is stale.
        if (!completed) return;
        // this is responsible for mpgoalcalculator loading and stepper behaviours
        const latestResponses = getEwsResponsesState();
        ewsPayloads.forEach(({ key }) => {
            ewsResponses[key] = latestResponses[key] ?? ewsResponses[key];
        });
    } else {
        try {
            const response = await ews(payload, { clientemail });
            ewsPayloads.forEach(({ key }) => {
                const termResult = response?.[key];
                if (termResult?.response) {
                    ewsResponses[key] = termResult;
                } else {
                    ewsResponses[key] = { error: true, err: termResult ?? response };
                }
            });
        } catch (err) {
            console.error(err);
            ewsPayloads.forEach(({ key }) => {
                ewsResponses[key] = { error: true, err };
            });
        }
    }

    dispatchMergedEwsState(dispatch, adviceGoals, adviceClient, ewsRequests, ewsResponses);
    dispatch(setEnableConfirmConsoleNavigation(true));
};

export const retrySingleEWS = async (
    dispatch: any,
    incomeSourceTypes: any,
    investmentAccountTypes: any,
    adviceClient: any,
    clientemail: string,
    durationKey: EwsDurationKey,
    goalCategoryPortfolioMappings: goalCategoryPortfolioMappingsType | undefined,
    reallocSchedules?: CallRunpipeArgs['realloc_schedules']
) => {
    const adviceGoals = store.getState().adviceGoals;
    const duration = EWS_DURATION_MAP[durationKey];
    if (!duration) return;
    const { data } = buildEwsDurationPayload(
        adviceClient,
        incomeSourceTypes,
        investmentAccountTypes,
        adviceGoals,
        duration,
        adviceGoals?.tenant,
        goalCategoryPortfolioMappings,
        reallocSchedules
    );

    if (!data.goalProfileList?.length) return;
    if (!data.investmentDetails?.length) return;

    const currentEwsRequests = { ...getEwsRequestsState() };
    const currentEwsResponses = { ...getEwsResponsesState() };
    currentEwsRequests[durationKey] = data;
    currentEwsResponses[durationKey] = { loading: true };

    dispatchEwsGoalsSync(dispatch, adviceGoals, currentEwsRequests, currentEwsResponses);

    // Progressive loading: retry this single term over the unified SSE stream. The stream expects
    // the common fields hoisted and the term nested under `durations`, then merges the ews +
    // runpipe chunks into the store per-term as they arrive (mirrors callUnifiedEWS).
    if (checkFFEnabled(FEATURE_NAMES['FUNDINGNAV_EWS_STREAMING_FE_AEX-3495'])) {
        const { dob, retirementAge, riskProfile, from_tenant, reallocate, isNewRiskProfile, ...durationData } =
            data as Record<string, any>;
        const streamPayload = {
            dob,
            retirementAge,
            riskProfile,
            from_tenant,
            ...(reallocate !== undefined && { reallocate }),
            ...(isNewRiskProfile !== undefined && { isNewRiskProfile }),
            durations: { [durationKey]: durationData }
        };
        let completed = true;
        try {
            completed = await runUnifiedEwsStream(dispatch, streamPayload, clientemail, { abortPrevious: false });
            const latestResponses = getEwsResponsesState();
            currentEwsResponses[durationKey] = latestResponses[durationKey] ?? currentEwsResponses[durationKey];
        } catch (err) {
            currentEwsResponses[durationKey] = { error: true, err };
        } finally {
            // Aborted when the user left the plan: adviceGoals here is stale.
            if (completed) {
                dispatchMergedEwsState(dispatch, adviceGoals, adviceClient, currentEwsRequests, currentEwsResponses);
                dispatch(setEnableConfirmConsoleNavigation(true));
            }
        }
        return;
    }

    try {
        const response: any = await ews(data, { clientemail });
        if (response?.response) {
            currentEwsResponses[durationKey] = response;
        } else {
            currentEwsResponses[durationKey] = { error: true, err: response };
        }
    } catch (err) {
        currentEwsResponses[durationKey] = { error: true, err };
    } finally {
        dispatchMergedEwsState(dispatch, adviceGoals, adviceClient, currentEwsRequests, currentEwsResponses);
        dispatch(setEnableConfirmConsoleNavigation(true));
    }
};

/**
 * Returns goalIds for which EWS itself succeeded for the given duration but the
 * downstream runpipe call did not produce a usable response (missing entry,
 * missing analysisReport, or explicit error).
 */
export const getFailedRunpipeGoalIds = (ewsResponse: any): string[] => {
    if (!ewsResponse || !ewsResponse?.response?.body) return [];
    const expected: string[] = (ewsResponse?.goePayload?.runpipe || []).map((r: any) => r?.goalId).filter(Boolean);
    if (!expected.length) return [];
    const runPipeResponses = ewsResponse?.response?.body?.runPipeResponses || [];
    return expected.filter((goalId: string) => {
        const entry = runPipeResponses.find((r: any) => r?.goalId === goalId);
        if (!entry) return true;
        // No-eligible-funding is a valid terminal state, not a failed runpipe to retry.
        if (entry?.noEligibleFundings) return false;
        return !entry?.response?.body?.analysisReport || entry?.error;
    });
};

/** EWS succeeded but one or more downstream runpipe calls failed for the duration. */
export const hasFailedRunpipesForDuration = (adviceGoals: any, durationKey: EwsDurationKey, dispatch: any): boolean => {
    const ewsResponse = getEwsResponsesState()?.[durationKey];
    if (!ewsResponse || ewsResponse.loading) return false;
    if (!ewsResponse?.response?.body) return false;
    const hasFailedRunpipes = getFailedRunpipeGoalIds(ewsResponse).length > 0;
    if (hasFailedRunpipes) {
        dispatch(logEventToBackEnd('RUNPIPE_FAILURE_WITH_EWS_SUCCESS'));
    }
    return hasFailedRunpipes;
};

/**
 * Retry only the failed runpipe calls for a given EWS duration without re-triggering EWS.
 * Backend identifies the retry by the presence of the `goePayload` array (containing the
 * runpipe_request payloads of the previously failed goals) alongside the standard EWS body.
 */
export const retryFailedRunpipes = async (
    dispatch: any,
    incomeSourceTypes: any,
    investmentAccountTypes: any,
    adviceClient: any,
    adviceGoals: any,
    clientemail: string,
    tenant: string,
    durationKey: EwsDurationKey,
    goalCategoryPortfolioMappings: goalCategoryPortfolioMappingsType | undefined,
    reallocSchedules?: CallRunpipeArgs['realloc_schedules']
) => {
    const duration = EWS_DURATION_MAP[durationKey];
    if (!duration) return;
    const previousEwsResponse = getEwsResponsesState()?.[durationKey];
    const failedGoalIds = getFailedRunpipeGoalIds(previousEwsResponse);
    if (!failedGoalIds.length) return;

    const goePayloadForFailed = (previousEwsResponse?.goePayload?.runpipe || [])
        .filter((r: any) => failedGoalIds.includes(r?.goalId))
        .map((r: any) => ({ goalId: r?.goalId, reallocSchedules, ...r?.runpipe_request }))
        .filter(Boolean);

    if (!goePayloadForFailed.length) return;

    const { data } = buildEwsDurationPayload(
        adviceClient,
        incomeSourceTypes,
        investmentAccountTypes,
        adviceGoals,
        duration,
        tenant,
        goalCategoryPortfolioMappings,
        reallocSchedules
    );

    if (!data.goalProfileList?.length) return;
    if (!data.investmentDetails?.length) return;

    const retryPayload = { ...data, goePayload: goePayloadForFailed };

    const currentEwsRequests = { ...getEwsRequestsState() };
    const currentEwsResponses = { ...getEwsResponsesState() };
    currentEwsRequests[durationKey] = data;
    // Mark as loading while preserving the existing successful runpipe data so it can be merged after retry.
    currentEwsResponses[durationKey] = { ...previousEwsResponse, loading: true, error: false };

    dispatchEwsGoalsSync(dispatch, adviceGoals, currentEwsRequests, currentEwsResponses);

    try {
        const response: any = await ews(retryPayload, { clientemail });
        const newRunPipeResponses: any[] = response?.body?.runPipeResponses || [];

        if (response?.body && newRunPipeResponses.length > 0) {
            // Merge: keep previous successful runpipe entries and overlay the newly retried ones.
            const existingRunPipeResponses: any[] = previousEwsResponse?.response?.body?.runPipeResponses || [];
            const runPipeMap = new Map<string, any>();
            existingRunPipeResponses.forEach((r: any) => {
                if (r?.goalId && r?.response?.body?.analysisReport) runPipeMap.set(r.goalId, r);
            });
            newRunPipeResponses.forEach((r: any) => {
                if (r?.goalId) runPipeMap.set(r.goalId, r);
            });

            // Merge goePayload.runpipe similarly so downstream consumers find runpipe_request for every goal.
            const existingGoePayloads: any[] = previousEwsResponse?.goePayload?.runpipe || [];
            const newGoePayloads: any[] = response?.goePayload?.runpipe || [];
            const goePayloadMap = new Map<string, any>();
            existingGoePayloads.forEach((g: any) => {
                if (g?.goalId) goePayloadMap.set(g.goalId, g);
            });
            newGoePayloads.forEach((g: any) => {
                if (g?.goalId) goePayloadMap.set(g.goalId, g);
            });
            currentEwsResponses[durationKey] = {
                ...previousEwsResponse,
                ...response,
                loading: false,
                error: false,
                response: {
                    ...previousEwsResponse?.response,
                    ...response,
                    body: {
                        ...previousEwsResponse?.response?.body,
                        ...response?.body,
                        runPipeResponses: Array.from(runPipeMap.values())
                    }
                },
                goePayload: {
                    ...previousEwsResponse?.goePayload,
                    ...response?.goePayload,
                    runpipe: Array.from(goePayloadMap.values())
                }
            };
        } else {
            // Retry call itself did not return runpipe responses — preserve previous data and clear loading
            // so the failed-runpipe state is detected again and the retry option is shown.
            currentEwsResponses[durationKey] = { ...previousEwsResponse, loading: false };
        }
    } catch (err) {
        console.error('retryFailedRunpipes failed:', err);
        currentEwsResponses[durationKey] = { ...previousEwsResponse, loading: false };
    } finally {
        dispatchMergedEwsState(dispatch, adviceGoals, adviceClient, currentEwsRequests, currentEwsResponses);
        dispatch(setEnableConfirmConsoleNavigation(true));
    }
};

/**
 * Retry a single goal's runpipe via the `/api/advice/runpipe_proxy` endpoint (a plain POST, not the
 * EWS stream) and merge the returned result into that duration's runPipeResponses in redux.
 */
export const retrySingleRunpipe = async (
    dispatch: any,
    durationKey: EwsDurationKey,
    goalId: string,
    runpipeGoePayload: any,
    tenant?: string
): Promise<{ success: boolean }> => {
    if (!goalId || !runpipeGoePayload) return { success: false };

    const payload = {
        goePayload: runpipeGoePayload,
        from_tenant: tenant ?? store.getState().adviceGoals?.tenant
    };

    try {
        const response: any = await runpipeProxy(payload, {
            'Content-Type': 'application/json',
            clientemail: store.getState().global?.orgInfo?.orgEmail || ''
        });
        const runpipeResult = response?.response;

        const adviceGoals = store.getState().adviceGoals;
        const adviceClient = store.getState().adviceClient;
        const previousEwsResponse = getEwsResponsesState()?.[durationKey];

        // Overlay the retried goal's runpipe result onto the existing runPipeResponses for this term.
        const runPipeMap = new Map<string, any>();
        (previousEwsResponse?.response?.body?.runPipeResponses || []).forEach((r: any) => {
            if (r?.goalId) runPipeMap.set(r.goalId, r);
        });
        runPipeMap.set(goalId, {
            //get all previous data of this runpipe and overlay the new response
            ...runPipeMap.get(goalId),
            goalId,
            response: runpipeResult,
            success: runpipeResult?.statusCode == 200
        });

        const currentEwsRequests = { ...getEwsRequestsState() };
        const currentEwsResponses = { ...getEwsResponsesState() };
        currentEwsResponses[durationKey] = {
            ...previousEwsResponse,
            response: {
                ...previousEwsResponse?.response,
                body: {
                    ...previousEwsResponse?.response?.body,
                    runPipeResponses: Array.from(runPipeMap.values())
                }
            }
        };

        dispatchMergedEwsState(dispatch, adviceGoals, adviceClient, currentEwsRequests, currentEwsResponses);
        return { success: runpipeResult?.success !== false };
    } catch (err) {
        console.error('retrySingleRunpipe failed:', err);
        return { success: false };
    }
};

// AEX-3108: a single-portfolio flow2 tenant has no term mappings, so the rebalancer gets one `default`
export const getRebalancerPortfolioMappings = (
    clientData: any,
    goalCategoryPortfolioMappings: goalCategoryPortfolioMappingsType | undefined
): goalCategoryPortfolioMappingsType => {
    const state = store.getState();
    const tenant = clientData?.tenant || state?.auth?.user?.tenant;
    if (isMultiPortfolioTenant() || getClientJourneyFlowType(tenant) !== 'flow2') {
        return goalCategoryPortfolioMappings || {};
    }
    return {
        [DEFAULT_GOAL_CATEGORY]: {
            portfolioBundleName: '',
            supportedWeightType: state?.global?.globalConfig?.tenant_supported_weight_type || 'both'
        }
    };
};

export const callPlanExecution = async (
    goalData: any,
    clientData: any,
    goalCategoryPortfolioMappings: goalCategoryPortfolioMappingsType,
    dispatch: any,
    forceRefresh = false
) => {
    // On an explicit retry (forceRefresh) always re-invoke the Rebalancer API, even when a
    // stale/partial planExecutionResponse already exists; otherwise skip if we already have results.
    if (!forceRefresh && Object.values(goalData?.global?.planExecutionResponse?.rebalancerResult || [])?.length > 0)
        return;
    let payload: any = {
        proposalId: goalData?._id,
        accounts: {
            incomeSources: clientData?.accounts?.incomeSources || [],
            investmentsSources: clientData?.accounts?.investmentsSources || []
        },
        ewsResponses: isMultiPortfolioTenant()
            ? {
                  longTerm: null,
                  mediumTerm: null,
                  shortTerm: null
              }
            : { [DEFAULT_GOAL_CATEGORY]: null },
        goalCategoryPortfolioMappings: getRebalancerPortfolioMappings(clientData, goalCategoryPortfolioMappings)
    };
    getTenantEwsDurations().forEach((goalCategory: EwsDuration) => {
        const ewsResponsesState = getEwsResponsesState();
        let termLevelews = ewsResponsesState?.[EWS_DURATION_KEYS[goalCategory]]?.response?.body?.ews || null;
        let termLevelRunPipe =
            ewsResponsesState?.[EWS_DURATION_KEYS[goalCategory]]?.response?.body?.runPipeResponses || null;
        let termLevelContributionSchedules =
            ewsResponsesState?.[EWS_DURATION_KEYS[goalCategory]]?.response?.body?.contributionSchedules || [];

        if (termLevelews && termLevelRunPipe) {
            payload.ewsResponses[EWS_DURATION_KEYS[goalCategory]] = {
                ews: termLevelews.map((termEws: any) => ({
                    accountId: termEws?.accountId || null,
                    firstContributionAmount: termEws?.firstContributionAmount || null,
                    firstContributionDate: termEws?.firstContributionDate || null,
                    goalId: termEws?.goalId || null,
                    lumpsumAmount: termEws?.lumpsumAmount ?? null,
                    contributionSchedule:
                        termLevelContributionSchedules
                            .find((schedule: any) => schedule?.goalId === termEws?.goalId)
                            ?.accounts?.find((account: any) => account?.accountId === termEws?.accountId)
                            ?.dateValueList || []
                })),
                runPipeResponses: termLevelRunPipe.map((termRunPipe: any) => ({
                    goalId: termRunPipe?.goalId || null,
                    recommendedPortfolioId: termRunPipe?.response?.body?.analysisReport?.recommendedPortfolioId || null,
                    goalCategory: goalCategory || null
                }))
            };
        }
    });

    try {
        await dispatch(getPlanExecution(payload));
    } catch (error) {
        console.error('Plan Execution Error: ', error);
    } finally {
        dispatch(setEnableConfirmConsoleNavigation(true));
    }
};

export const priorityOptionsFlow2 = (t: any, goalPriorityEnum?: Record<string, string>) => {
    const enumToUse = goalPriorityEnum || Flow2GoalPriorityEnum;
    return Object.keys(enumToUse).map((key, index) => {
        const enumValue = enumToUse[key as keyof typeof enumToUse];
        const translationKey = `${enumValue.toUpperCase()}`;
        return {
            label: t(translationKey),
            value: enumValue,
            index: index
        };
    });
};

// Only banortemex surfaces the `wish` priority as "Aspire"; every other tenant keeps the option's own label.
export const isAspirePriorityTenant = (tenant?: string): boolean => tenant === 'banortemex';

export const getPriorityLabel = (
    priorityOption: { value?: string; label?: string } | undefined,
    tenant: string | undefined,
    // eslint-disable-next-line no-unused-vars
    t: (key: string) => string
): string | undefined =>
    isAspirePriorityTenant(tenant) && priorityOption?.value?.toLowerCase() === 'wish'
        ? t('ASPIRE')
        : priorityOption?.label;

export const getGoalPriorityEnum = (goalPriorityValue: any) => {
    const result: Record<string, string> = {};

    for (const key in goalPriorityValue) {
        const upperKey = key.toUpperCase();
        const label = key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();

        result[upperKey] = label;
    }

    return result;
};

export const getClientJourneyFlowType = (tenant: string): string => {
    if (!checkFFEnabled(FEATURE_NAMES['DA_FLOW2_FE_AEX-2751_AEX-2626']) || !tenant) {
        return 'DEFAULT';
    }

    // Find which flow the tenant belongs to
    for (const [flowType, tenants] of Object.entries(appFlowDictionary)) {
        if (tenants.includes(tenant)) {
            return flowType;
        }
    }

    return 'DEFAULT';
};

/**
 * Shared predicate for the flow2 + multi-portfolio tenant combination (`isFlow2 && isMultiPortfolioTenant`).
 * Centralized so every call site reuses one definition instead of re-inlining the expression.
 */
export const isFlow2MultiPortfolioTenant = (isFlow2: boolean, isMultiPortfolioTenant: boolean): boolean =>
    isFlow2 && isMultiPortfolioTenant;

let latestMpGoalCalculatorRunId = 0;

export const callMpGoalCalculator = async (
    dispatch: any,
    adviceGoals: AdviceGoalPayloadType,
    orgEmail: string,
    goalCategoryPortfolioMappings: goalCategoryPortfolioMappingsType | undefined
) => {
    const runId = ++latestMpGoalCalculatorRunId;
    const syncIfLatestRun = (results: any[]) => {
        if (runId === latestMpGoalCalculatorRunId) dispatchGoalCalculatorSync(dispatch, results);
    };
    try {
        const formFields = adviceGoals?.formFields ?? [];
        const responses = adviceGoals?.response ?? [];
        const responsesGC = adviceGoals?.global?.responseGoalCalculator ?? [];
        const headers = getGOEHeaders(orgEmail);

        const mpGoalCalculatorResults: any[] = formFields.map((goal: any) => ({ goalId: goal.goalId, loading: true }));
        // Initial dispatch to set loading state for all goals
        syncIfLatestRun(mpGoalCalculatorResults);

        const mpGoalCalculatorPromises = formFields.map((goal: any, index: number) => {
            const matchingResponse = responses.find((res: any) => res.goalId === goal.goalId);
            // When the response carries no goePayload (empty runPipeResponses), fall back to the
            // term's runpipe goePayload in the ews slice, matched by goalId.
            const goePayload =
                matchingResponse?.goePayload ??
                getEwsResponsesState()?.[getGoalDurationKey(goal)]?.goePayload?.runpipe?.find(
                    (r: any) => r?.goalId === goal.goalId
                )?.runpipe_request;
            const matchingResponseGC = responsesGC.find((res: any) => res.goalId === goal.goalId);
            const hasValidResponse =
                !matchingResponseGC?.loading && !matchingResponseGC?.error && matchingResponseGC?.response;

            if (hasValidResponse) {
                mpGoalCalculatorResults[index] = matchingResponseGC;
                syncIfLatestRun(mpGoalCalculatorResults);
                return Promise.resolve(matchingResponseGC);
            }

            if (!goePayload) {
                mpGoalCalculatorResults[index] = { goalId: goal.goalId, loading: false };
                syncIfLatestRun(mpGoalCalculatorResults);
                return Promise.resolve({ goalId: goal.goalId, loading: false });
            }

            const payload = buildGoalCalculatorPayload(goal, goePayload, goalCategoryPortfolioMappings);

            return mpgoalcalculator(payload, headers)
                .then((response: any) => {
                    if (response?.error) {
                        const errorResult = { goalId: goal.goalId, loading: false, error: true, err: response.error };
                        mpGoalCalculatorResults[index] = errorResult;
                        syncIfLatestRun(mpGoalCalculatorResults);
                        return errorResult;
                    }
                    const result = {
                        goalId: goal.goalId,
                        goalName: goal?.data?.goalName,
                        loading: false,
                        ...response
                    };
                    mpGoalCalculatorResults[index] = result;
                    syncIfLatestRun(mpGoalCalculatorResults);
                    dispatch(setEnableConfirmConsoleNavigation(true));
                    return result;
                })
                .catch((err) => {
                    const errorResult = { goalId: goal.goalId, loading: false, error: true, err };
                    mpGoalCalculatorResults[index] = errorResult;
                    syncIfLatestRun(mpGoalCalculatorResults);
                    dispatch(setEnableConfirmConsoleNavigation(true));
                    return errorResult;
                });
        });

        await Promise.allSettled(mpGoalCalculatorPromises);
    } catch (error) {
        console.error('Error in callMpGoalCalculator:', error);
    }
};

export const retrySingleMpGoalCalculator = async (
    dispatch: any,
    adviceGoals: AdviceGoalPayloadType,
    orgEmail: string,
    goalId: string,
    goalCategoryPortfolioMappings: goalCategoryPortfolioMappingsType | undefined
) => {
    try {
        const formFields = adviceGoals?.formFields ?? [];
        const responses = adviceGoals?.response ?? [];
        const responsesGC = adviceGoals?.global?.responseGoalCalculator ?? [];
        const headers = getGOEHeaders(orgEmail);
        const goalIndex = formFields.findIndex((g: any) => g.goalId === goalId);
        const goal = formFields[goalIndex];

        if (!goal || goalIndex === -1) return;

        const getLatestAdviceGoals = () => store.getState().adviceGoals;

        let latestState = getLatestAdviceGoals();
        let currentResults = [...(latestState?.global?.responseGoalCalculator ?? [])];
        currentResults[goalIndex] = { goalId: goal.goalId, loading: true };

        dispatchGoalCalculatorSync(dispatch, currentResults);

        const matchingResponse = responses.find((res: any) => res.goalId === goal.goalId);
        // When the response carries no goePayload (empty runPipeResponses), fall back to the
        // term's runpipe goePayload in the ews slice, matched by goalId.
        const goePayload =
            matchingResponse?.goePayload ??
            getEwsResponsesState()?.[getGoalDurationKey(goal)]?.goePayload?.runpipe?.find(
                (r: any) => r?.goalId === goal.goalId
            )?.runpipe_request;
        const matchingResponseGC = responsesGC.find((res: any) => res.goalId === goal.goalId);
        const isAlreadyLoading = matchingResponseGC?.loading;
        const hasValidResponse =
            !matchingResponseGC?.loading && !matchingResponseGC?.error && matchingResponseGC?.response;

        if (isAlreadyLoading || hasValidResponse) {
            return Promise.resolve(matchingResponseGC);
        }

        if (!goePayload) {
            latestState = getLatestAdviceGoals();
            currentResults = [...(latestState?.global?.responseGoalCalculator ?? [])];
            currentResults[goalIndex] = { goalId: goal.goalId };
            dispatchGoalCalculatorSync(dispatch, currentResults);
            return;
        }

        const payload = buildGoalCalculatorPayload(goal, goePayload, goalCategoryPortfolioMappings);

        const response: any = await mpgoalcalculator(payload, headers);

        latestState = getLatestAdviceGoals();
        currentResults = [...(latestState?.global?.responseGoalCalculator ?? [])];

        if (!response?.response) {
            currentResults[goalIndex] = { goalId: goal.goalId, error: true, err: response };
        } else {
            currentResults[goalIndex] = {
                goalId: goal.goalId,
                goalName: goal?.data?.goalName,
                ...response
            };
        }

        dispatchGoalCalculatorSync(dispatch, currentResults);
    } catch (error) {
        console.error('Error in retrySingleMpGoalCalculator:', error);
        const formFields = adviceGoals?.formFields ?? [];
        const goalIndex = formFields.findIndex((g: any) => g.goalId === goalId);
        const goal = formFields[goalIndex];
        const currentResults = [...(store.getState().adviceGoals?.global?.responseGoalCalculator ?? [])];
        currentResults[goalIndex] = { goalId: goal.goalId, error: true, err: error };
        dispatchGoalCalculatorSync(dispatch, currentResults);
    } finally {
        dispatch(setEnableConfirmConsoleNavigation(true));
    }
};

export const getLastUpdateDateFromGoals = (goals: any[]): string => {
    if (!goals || goals.length === 0) return '';
    const sortedGoals = goals
        .filter((goal) => goal.data?.updatedAt)
        .sort((a, b) => new Date(b.data?.updatedAt).getTime() - new Date(a.data?.updatedAt).getTime());
    return sortedGoals.length > 0 ? dayjs(sortedGoals[0].data?.updatedAt).format('DD MMM YYYY') : '';
};

export const getLastUpdateDateFromInvestmentAccounts = (accounts: any[]): string => {
    if (!accounts || accounts.length === 0) return '';
    const sortedAccounts = accounts
        .filter((account) => account?.updatedAt)
        .sort((a, b) => new Date(b?.updatedAt).getTime() - new Date(a?.updatedAt).getTime());
    return sortedAccounts.length > 0 ? dayjs(sortedAccounts[0]?.updatedAt).format('DD MMM YYYY') : '';
};

export const formatTimestampToDateInParts = (
    timestamp: string | undefined
): { day: string; month: string; year: string } => {
    const fallback = { day: '--', month: '', year: '' };
    if (!timestamp) return fallback;

    const parsed = dayjs(timestamp);
    if (!parsed.isValid()) return fallback;

    return {
        day: parsed.format('DD'),
        month: parsed.format('MMM'),
        year: parsed.format('YYYY')
    };
};

export const formatPdfFileDate = (timestamp?: string): string => {
    const parsed = dayjs(timestamp);
    return timestamp && parsed.isValid() ? parsed.format('MMDDYY') : dayjs().format('MMDDYY');
};

export const buildConsolePdfFileName = (doc: any): string => {
    const lastUpdateDate = doc?.lastUpdateDate || doc?.global?.lastUpdateDate;
    return `${doc?.clientFirstName ?? ''}_${doc?.clientLastName ?? ''}_${formatPdfFileDate(lastUpdateDate)}.pdf`;
};

export const isTenantAdmin = (user: any): boolean => {
    return user?.userRole === 'admin';
};

export const isOwnProposal = (user: any, proposal: any): boolean => {
    return user?.userId === proposal?.author_id;
};
